import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import styles from "./FiscalDevices.module.scss";
import _ from "lodash";
import Input from "../../../components/input/Input";
import {
  fetchFiscalDevices,
  selectAllFiscalDevices,
  selectFiscalDevice,
} from "../../../store/actions/admin/fiscalDevice";
import moment from "moment-timezone";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import ResourceList from "../../../components/resourceList/ResourceList";
import { isMobile } from "react-device-detect";
import { FilterIcon } from "../../../assets";
import { CloseIcon } from "../../../assets";
import { Switch } from "@material-ui/core";
import SecondaryButton from "../../../components/secondaryButton/SecondaryButton";
import fiscalDeviceService from "../../../apis/services/admin/fiscalDeviceService";
import Checkbox from "../../../components/checkbox/Checkbox";
import DropdownButton from "../../../components/dropdownButton/DropdownButton";
import UpdateJarVersionModal from "../../../components/modals/updateJarVersionModal/UpdateJarVersionModal";
import fiscalDeviceUpdateService from "../../../apis/services/admin/fiscalDeviceUpdateService";
import tagsService from "../../../apis/services/admin/tagsService";
import { toast } from "react-toastify";
import { getDeviceCertificate } from "../../../utils/deviceCertificate";

const ONLINE_REFRESH_INTERVAL_MS = 60 * 1000;

const HEADER_VALUES = [
  { label: "NAZIV", key: "name", sortable: true },
  { label: "FIRMA", key: "companyName", sortable: true },
  { label: "POSLOVNI PROSTOR", key: "businessUnit", sortable: true },
  { label: "TAGOVI", key: "tags", sortable: false, size: "sm" },
  { label: "ZDRAVLJE", key: "healthFlags", sortable: false, size: "sm" },
  { label: "ONLINE", key: "lastOnlineAt", sortable: true },
  { label: "CERT. VAŽI DO", key: "cardCertificateValidTo", sortable: true },
  { label: "KREIRAN", key: "createdAt", sortable: true },
  { label: "VERZIJA", key: "version", sortable: true, size: "sm" },
  { label: "RAČUN", key: "lastInvoiceTime", sortable: true },
];

// Helper: get contrast color (black/white) based on background
const getContrastColor = (hex) => {
  if (!hex) return "#000";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? "#000" : "#fff";
};

// Health status configuration
const HEALTH_STATUS_CONFIG = {
  CRITICAL: { color: "#B71C1C", bgColor: "#FFEBEE", icon: "🔴", label: "Kritično" },
  WARNING: { color: "#F57C00", bgColor: "#FFF3E0", icon: "⚠️", label: "Upozorenje" },
  OK: { color: "#2E7D32", bgColor: "#E8F5E9", icon: "✅", label: "OK" },
};

// Failure breakdown labels
const FAILURE_LABELS = {
  unknownController: "Nepoznat kontroler",
  unknownItem: "Nepoznat artikal",
  pinRequired: "Potreban PIN",
  pinVerification: "Greška PIN-a",
  cardNotPresent: "Kartica nije prisutna",
  sdcNotConfigured: "SDC nije konfigurisan",
  lpfrSubmit: "LPFR greška",
  vsdcSubmit: "VSDC greška",
  dbSave: "Greška baze",
  fscSave: "FSC greška",
  pdfGeneration: "PDF greška",
  printing: "Greška štampe",
};

// Helper to build tooltip content for critical issues
const getCriticalTooltip = (flags) => {
  const issues = [];
  if (flags.physicalInterventionRequired) issues.push("Potrebna fizička intervencija");
  if (flags.cardLocked) issues.push("Kartica zaključana");
  if (flags.hasDeadlock) issues.push("Deadlock detektovan");
  return issues.join(", ");
};

// Helper to build tooltip content for warnings
const getWarningTooltip = (flags) => {
  const issues = [];
  if (flags.highMemoryUsage) issues.push("Visoka memorija");
  if (flags.hasStuckTasks) {
    // Only show stuck tasks if explicitly flagged - stuckTaskNames comes from overdueTasks
    if (flags.stuckTaskNames && flags.stuckTaskNames.length > 0) {
      issues.push(`Zaglavljeni taskovi: ${flags.stuckTaskNames.join(", ")}`);
    } else {
      issues.push("Zaglavljeni taskovi");
    }
  }
  if (flags.hasOrphanedAuditPackages) issues.push("Orphan paketi");
  if (flags.hasPcscError) issues.push("PC/SC greška");
  if (flags.hasSmartCardError) issues.push("Greška smart kartice");
  if (flags.lowSuccessRate) {
    let msg = "Nizak success rate";
    // Add failure breakdown if available
    if (flags.failureBreakdown) {
      const failures = Object.entries(flags.failureBreakdown)
        .filter(([_, count]) => count > 0)
        .sort(([, a], [, b]) => b - a) // Sort by count descending
        .slice(0, 3) // Show top 3 failures
        .map(([key, count]) => `${FAILURE_LABELS[key] || key}: ${count}`)
        .join(", ");
      if (failures) {
        msg += ` (${failures})`;
      }
    }
    issues.push(msg);
  }
  if (flags.consecutiveSmartCardErrors) issues.push("Uzastopne greške");
  return issues.join("; ");
};

const FiscalDevices = () => {
  const dispatch = useDispatch();
  const fiscalDevices = useSelector(
    (state) => state.fiscalDevices.fiscalDevices
  );
  const {
    page,
    perPage,
    sortBy,
    order,
    term,
    filterOffline: filterOfflineStore,
    filterNoRecentInvoices: filterNoRecentInvoicesStore,
    offset: offsetStore,
    includeHealthFlags: includeHealthFlagsStore,
    includeTags: includeTagsStore,
    tagIds: tagIdsStore,
  } = fiscalDevices || [];
  const selectedFiscalDevices = useSelector(
    (state) => state.fiscalDevices.selectedFiscalDevices.data
  );
  const selectAll = useSelector(
    (state) => state.fiscalDevices.selectedFiscalDevices.selectAll
  );
  const [updateJarVersionModalOpen, setUpdateJarVersionModalOpen] =
    useState(false);
  // Support deep-linking from Intervend: /fiscal-devices?searchTerm=<MAC> pre-filters the list by MAC.
  const [searchTerm, setSearchTerm] = useState(
    () => new URLSearchParams(window.location.search).get('searchTerm') || term,
  );
  const history = useHistory();
  const [filterOffline, setFilterOffline] = useState(filterOfflineStore);
  const [filterNoRecentInvoices, setFilterNoRecentInvoices] = useState(
    filterNoRecentInvoicesStore
  );
  const [includeHealthFlags, setIncludeHealthFlags] = useState(
    includeHealthFlagsStore !== undefined ? includeHealthFlagsStore : true
  );
  const [includeTags, setIncludeTags] = useState(
    includeTagsStore !== undefined ? includeTagsStore : true
  );
  const [healthFilter, setHealthFilter] = useState("");
  
  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState(tagIdsStore || []);

  const [offset, setOffset] = useState(offsetStore);
  
  // Fetch all tags for filter dropdown
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await tagsService.fetchTags();
        setAllTags(response?.data || []);
      } catch (err) {
        console.error("Error fetching tags:", err);
      }
    };
    fetchTags();
  }, []);
  const [filterOpen, setFilterOpen] = useState(false);
  const searchFicalDevices = _.debounce((searchTerm) => {
    dispatch(
      fetchFiscalDevices(
        page,
        perPage,
        searchTerm,
        sortBy,
        order,
        filterOffline,
        filterNoRecentInvoices,
        offset,
        includeHealthFlags,
        includeTags,
        selectedTagIds.length > 0 ? selectedTagIds : null
      )
    );
  }, 600);

  useEffect(() => {
    dispatch(
      fetchFiscalDevices(
        page,
        perPage,
        searchTerm,
        sortBy,
        order,
        filterOffline,
        filterNoRecentInvoices,
        offset,
        includeHealthFlags,
        includeTags,
        selectedTagIds.length > 0 ? selectedTagIds : null
      )
    );
  }, [filterOffline, filterNoRecentInvoices, includeHealthFlags, includeTags, selectedTagIds]);

  // The online dot is derived from lastOnlineAt vs. "now" on every render, so a
  // list fetched once goes red after ~5 min even though the device keeps pinging.
  // Refresh the current view in the background (silent: no loader) so the dot
  // tracks reality without the user reloading the page.
  const latestQuery = useRef();
  latestQuery.current = {
    page,
    perPage,
    searchTerm,
    sortBy,
    order,
    filterOffline,
    filterNoRecentInvoices,
    offset,
    includeHealthFlags,
    includeTags,
    tagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
  };
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      const q = latestQuery.current;
      dispatch(
        fetchFiscalDevices(
          q.page,
          q.perPage,
          q.searchTerm,
          q.sortBy,
          q.order,
          q.filterOffline,
          q.filterNoRecentInvoices,
          q.offset,
          q.includeHealthFlags,
          q.includeTags,
          q.tagIds,
          true
        )
      );
    }, ONLINE_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dispatch]);

  const onLoadPage = (page) => {
    dispatch(
      fetchFiscalDevices(
        page,
        perPage,
        searchTerm,
        sortBy,
        order,
        filterOffline,
        filterNoRecentInvoices,
        offset,
        includeHealthFlags,
        includeTags,
        selectedTagIds.length > 0 ? selectedTagIds : null
      )
    );
  };

  const onSort = (sortBy, order) => {
    dispatch(
      fetchFiscalDevices(
        page,
        perPage,
        searchTerm,
        sortBy,
        order,
        filterOffline,
        filterNoRecentInvoices,
        offset,
        includeHealthFlags,
        includeTags,
        selectedTagIds.length > 0 ? selectedTagIds : null
      )
    );
  };

  const onSubmitUpdate = async ({ jarFileUrl, fiscalDeviceIds, date }) => {
    try {
      fiscalDeviceUpdateService.createUpdate({
        jarFileUrl,
        date,
        fiscalDeviceIds,
      });
      toast.info("Update kreiran");
      setUpdateJarVersionModalOpen(false);
    } catch (err) { }
  };

  const onSelectFiscalDevice = (fiscalDevice) => {
    history.push(`/fiscal-devices/${fiscalDevice.id}/info`);
  };

  const onSelectFiscalDeviceCheckbox = (fiscalDevice) => {
    dispatch(selectFiscalDevice(fiscalDevice));
  };

  const isFiscalDeviceSelected = (fiscalDevice) => {
    const found = selectedFiscalDevices.find(
      (element) => element.id === fiscalDevice.id
    );
    if (found) {
      return true;
    } else {
      return false;
    }
  };

  const dropdownActions = [
    {
      name: "Update",
      onClick: () => {
        setUpdateJarVersionModalOpen(true);
      },
    },
    {
      name: "Delete",
      onClick: () => { },
    },
  ];

  // Render tags badges
  const renderTags = (tags) => {
    if (!tags || tags.length === 0) {
      return <span style={{ color: "#999" }}>—</span>;
    }
    return (
      <div className={styles.tagsContainer}>
        {tags.slice(0, 2).map((tag) => (
          <span
            key={tag.id}
            className={styles.tagBadge}
            style={{
              backgroundColor: tag.color,
              color: getContrastColor(tag.color),
            }}
            title={tag.name}
          >
            {tag.name}
          </span>
        ))}
        {tags.length > 2 && (
          <span className={styles.moreTagsBadge} title={tags.slice(2).map(t => t.name).join(", ")}>+{tags.length - 2}</span>
        )}
      </div>
    );
  };

  // Render health status badge
  const renderHealthBadge = (healthFlags) => {
    if (!healthFlags || !healthFlags.hasHealthData) {
      return (
        <span className={styles.healthBadge} style={{ backgroundColor: "#E0E0E0", color: "#666" }}>
          —
        </span>
      );
    }

    const status = healthFlags.overallStatus;
    const config = HEALTH_STATUS_CONFIG[status] || { color: "#666", bgColor: "#E0E0E0", icon: "—", label: "—" };
    
    let tooltipText = "";
    if (status === "CRITICAL") {
      tooltipText = getCriticalTooltip(healthFlags);
    } else if (status === "WARNING") {
      tooltipText = getWarningTooltip(healthFlags);
    } else if (status === "OK") {
      tooltipText = "Uređaj radi normalno";
    }

    return (
      <span 
        className={styles.healthBadge} 
        style={{ backgroundColor: config.bgColor, color: config.color }}
        title={tooltipText}
      >
        {config.icon} {config.label}
      </span>
    );
  };

  const getDeviceParamValue = (
    device,
    key,
    lastTimeOnline,
    createdAt,
    isOnline,
    lastInvoiceTime
  ) => {
    const certificate = getDeviceCertificate(device);
    switch (key) {
      case HEADER_VALUES[0].key: // NAZIV
        return device.name;
      case HEADER_VALUES[1].key: // FIRMA
        return device.businessUnit.company.name;
      case HEADER_VALUES[2].key: // POSLOVNI PROSTOR
        const address = device.businessUnit.address;
        const formattedAddress = `${address.name}, ${address.locality} ${address.district} ${address.countryCode}`
        return formattedAddress;
      case HEADER_VALUES[3].key: // TAGOVI
        return renderTags(device.tags);
      case HEADER_VALUES[4].key: // ZDRAVLJE
        return renderHealthBadge(device.healthFlags);
      case HEADER_VALUES[5].key: // ONLINE
        return (
          <div className={styles.cell}>
            <div
              className={classNames(isOnline ? styles.online : styles.ofline)}
            />
            <p>{lastTimeOnline}</p>
          </div>
        );
      case HEADER_VALUES[6].key: // CERT. VAŽI DO
        // Format the certificate valid date
        return certificate?.validTo
          ? moment(certificate.validTo)
            .tz(moment.tz.guess())
            .format("DD-MM-YYYY")
          : "-";
      case HEADER_VALUES[7].key: // KREIRAN
        return createdAt;
      case HEADER_VALUES[8].key: // VERZIJA
        return device.version;
      case HEADER_VALUES[9].key: // RAČUN
        return lastInvoiceTime;
      default:
        return "";
    }
  };

  const renderResourceListRow = (device, index) => {
    const certificate = getDeviceCertificate(device);
    const lastTimeOnline = device.lastOnlineAt
      ? moment(device.lastOnlineAt)
        .tz(moment.tz.guess())
        .format("DD-MM-YYYY HH:mm:ss")
      : "";

    const lastInvoiceTime = device.lastInvoiceTime
      ? moment(device.lastInvoiceTime)
        .tz(moment.tz.guess())
        .format("DD-MM-YYYY HH:mm:ss")
      : "";

    const isVirtualPFR = !device.salesDataController;
    const isOnline = isVirtualPFR
      ? true
      : new Date().getTime() - device.lastOnlineAt > 300 * 1000 ? false : true;

    const createdAt = device.createdAt
      ? moment(device.createdAt)
        .tz(moment.tz.guess())
        .format("DD-MM-YYYY HH:mm:ss")
      : "";

    const address = device.businessUnit.address;
    const formattedAddress = address.locality ? `${address.name}, ${address.locality}, ${address.district}, ${address.countryCode}` : address.name

    // Apply client-side health filter
    if (healthFilter && device.healthFlags?.overallStatus !== healthFilter) {
      return null;
    }

    if (isMobile) {
      return (
        <div
          className={styles.card}
          onClick={() => onSelectFiscalDevice(device)}
          key={device.id}
        >
          {HEADER_VALUES.map((value, idx) => (
            <div className={styles.rowCard} key={idx}>
              <p className={styles.rowCardTitle}>
                {value.label.charAt(0).toUpperCase() +
                  value.label.slice(1).toLowerCase()}
              </p>
              <p className={styles.rowCardValue}>
                {getDeviceParamValue(
                  device,
                  value.key,
                  lastTimeOnline,
                  createdAt,
                  isOnline,
                  lastInvoiceTime
                )}
              </p>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <div
          className={classNames(
            styles.row,
            index % 2 === 0 ? styles.even : styles.odd
          )}
          key={device.id}
          onClick={() => onSelectFiscalDevice(device)}
        >
          <div className={styles.cell}>
            {true && (
              <div className={styles.checkBoxWrap}>
                <Checkbox
                  onCheck={() => onSelectFiscalDeviceCheckbox(device)}
                  checked={isFiscalDeviceSelected(device)}
                />
              </div>
            )}
            <a
              href={`https://sdf.rs/fiscal-devices/${device.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.aTag}
            >
              <p
                className={classNames(styles.configLabel, styles.clickable)}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {device.name}
              </p>
            </a>
          </div>
          <div className={styles.cell}>
            <p>{device.businessUnit.company.name}</p>
          </div>
          <div className={styles.cell}>
            <p>{formattedAddress}</p>
          </div>
          <div className={classNames(styles.cell, styles.cellSm)}>
            {renderTags(device.tags)}
          </div>
          <div className={classNames(styles.cell, styles.cellSm)}>
            {renderHealthBadge(device.healthFlags)}
          </div>
          <div className={styles.cell}>
            <div
              className={classNames(isOnline ? styles.online : styles.ofline)}
            />
            <p>{lastTimeOnline}</p>
          </div>
          <div className={styles.cell}>
            <p>
              {certificate?.validTo
                ? moment(certificate.validTo)
                  .tz(moment.tz.guess())
                  .format("DD-MM-YYYY")
                : "-"}
            </p>
          </div>
          <div className={styles.cell}>
            <p>{createdAt}</p>
          </div>
          <div className={styles.cell}>
            <p>{device.version}</p>
          </div>
          <div className={styles.cell}>
            <p>{lastInvoiceTime}</p>
          </div>
        </div>
      );
    }
  };

  return (
    <DashboardLayout>
      <>
        <UpdateJarVersionModal
          modalOpen={updateJarVersionModalOpen}
          onClose={() => {
            setUpdateJarVersionModalOpen(false);
          }}
          selectedFiscalDevices={selectedFiscalDevices}
          onRemoveDeviceFromList={(device) => {
            dispatch(selectFiscalDevice(device));
          }}
          onSubmit={onSubmitUpdate}
        />
        <div
          className={classNames(
            filterOpen ? styles.filterWrap : styles.filterWrapClosed
          )}
        >
          <div className={styles.filterHeader}>
            <div
              className={styles.closeIconWrap}
              onClick={() => {
                setFilterOpen(false);
              }}
            >
              <img src={CloseIcon} alt="close" className={styles.closeIcon} />
            </div>
            <p className={styles.filters}>Filteri</p>
          </div>
          <div className={styles.filterContent}>
            <div className={styles.inputWrapper}>
              <p>Prikaži samo offline uređaje</p>
              <Switch
                color="primary"
                checked={filterOffline}
                onChange={(event) => {
                  setFilterOffline(event.target.checked);
                  setFilterOpen(false);
                }}
              />
            </div>
            <div className={styles.inputWrapper}>
              <p>Prikaži samo bez skorasnjih računa</p>
              <Switch
                color="primary"
                checked={filterNoRecentInvoices}
                onChange={(event) => {
                  setFilterNoRecentInvoices(event.target.checked);
                  setFilterOpen(false);
                }}
              />
              <p>Vreme bez računa (minut)</p>
              <Input
                name="Offset"
                value={offset}
                onChange={setOffset}
                type="number"
              />
            </div>
            <div className={styles.inputWrapper}>
              <p>Uključi zdravlje uređaja</p>
              <Switch
                color="primary"
                checked={includeHealthFlags}
                onChange={(event) => {
                  setIncludeHealthFlags(event.target.checked);
                }}
              />
            </div>
            <div className={styles.inputWrapper}>
              <p>Filtriraj po zdravlju</p>
              <select
                className={styles.healthSelect}
                value={healthFilter}
                onChange={(e) => setHealthFilter(e.target.value)}
              >
                <option value="">Svi</option>
                <option value="CRITICAL">🔴 Kritično</option>
                <option value="WARNING">⚠️ Upozorenja</option>
                <option value="OK">✅ OK</option>
              </select>
            </div>
            <div className={styles.inputWrapper}>
              <p>Uključi tagove</p>
              <Switch
                color="primary"
                checked={includeTags}
                onChange={(event) => {
                  setIncludeTags(event.target.checked);
                }}
              />
            </div>
            {allTags.length > 0 && (
              <div className={styles.inputWrapper}>
                <p>Filtriraj po tagovima</p>
                <div className={styles.tagFilterContainer}>
                  {allTags.map((tag) => (
                    <div
                      key={tag.id}
                      className={classNames(
                        styles.tagFilterItem,
                        selectedTagIds.includes(tag.id) && styles.tagFilterItemSelected
                      )}
                      style={{
                        backgroundColor: selectedTagIds.includes(tag.id) ? tag.color : "#f0f0f0",
                        color: selectedTagIds.includes(tag.id) ? getContrastColor(tag.color) : "#333",
                        borderColor: tag.color,
                      }}
                      onClick={() => {
                        if (selectedTagIds.includes(tag.id)) {
                          setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                        } else {
                          setSelectedTagIds([...selectedTagIds, tag.id]);
                        }
                      }}
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
                {selectedTagIds.length > 0 && (
                  <button
                    className={styles.clearTagsButton}
                    onClick={() => setSelectedTagIds([])}
                  >
                    Obriši sve tagove
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div
          className={classNames(
            styles.content,
            isMobile && styles.contentMobile
          )}
        >
          <div className={styles.topPart}>
            <div className={styles.header}>
              <p className={isMobile ? styles.titleMobile : styles.title}>
                Fiskalni uređaji
              </p>
              {isMobile && (
                <SecondaryButton
                  name="Izvezi u XLS"
                  onClick={() => {
                    fiscalDeviceService.downloadXLs();
                  }}
                />
              )}
            </div>

            <div className={styles.flexEnd}>
              {!isMobile && (
                <>
                  <div className={styles.downloadBtnWrap}>
                    <DropdownButton
                      buttonName={"Akcije"}
                      actions={dropdownActions}
                      disabled={selectedFiscalDevices.length === 0}
                    />
                  </div>
                  <div className={styles.downloadBtnWrap}>
                    <SecondaryButton
                      name="Izvezi u XLS"
                      onClick={() => {
                        fiscalDeviceService.downloadXLs();
                      }}
                    />
                  </div>
                </>
              )}
              <div
                className={classNames(
                  filterOffline || filterNoRecentInvoices || healthFilter || selectedTagIds.length > 0
                    ? styles.filterIconActive
                    : styles.filterIconWrap
                )}
              >
                <img
                  src={FilterIcon}
                  className={styles.filterIcon}
                  fill={"red"}
                  alt="left"
                  onClick={() => {
                    setFilterOpen(true);
                  }}
                />
              </div>
              <div className={styles.inputWrapper}>
                <Input
                  onChange={(e) => {
                    setSearchTerm(e);
                    searchFicalDevices(e);
                  }}
                  onFocus={() => {
                    dispatch(
                      fetchFiscalDevices(
                        1,
                        perPage,
                        searchTerm,
                        sortBy,
                        order,
                        filterOffline,
                        filterNoRecentInvoices,
                        offset,
                        includeHealthFlags,
                        includeTags,
                        selectedTagIds.length > 0 ? selectedTagIds : null
                      )
                    );
                  }}
                  value={searchTerm}
                  placeholder={searchTerm === "" && "Search"}
                />
              </div>
            </div>
          </div>
          <div
            className={classNames(
              styles.listWrapper,
              isMobile && styles.listWrapperMobile
            )}
          >
            <ResourceList
              resource={fiscalDevices}
              selectable={true}
              onSelectAll={() => {
                dispatch(selectAllFiscalDevices(!selectAll));
              }}
              selectAll={selectAll}
              headerValues={HEADER_VALUES}
              onLoadPage={onLoadPage}
              onSort={onSort}
              rowRender={renderResourceListRow}
              minHeight
              showLoader={true}
            />
          </div>
        </div>
      </>
    </DashboardLayout>
  );
};

export default FiscalDevices;
